// src/pages/ReceiptProcessor.jsx
// Procesamiento de recibos de sueldo: selecciona un lote, procesa el PDF
// y genera fichas individuales por persona.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { receiptsApi } from '../services/api';
import { useTexts, TXT } from '../context/TextContext';
import { useToast } from '../context/ToastContext';
import CustomSelect from '../components/CustomSelect';

const CONFIRM_DELETE_RECEIPTS_FALLBACK = '¿Está seguro que desea eliminar todas las fichas generadas? Esta acción no se puede deshacer.';

export default function ReceiptProcessor() {
  const navigate        = useNavigate();
  const { t }           = useTexts();
  const { showToast }   = useToast();

  const [lotes, setLotes]           = useState([]);
  const [loteId, setLoteId]         = useState('');
  const [processing, setProcessing] = useState(false);  // true mientras corre el job
  const [progress, setProgress]     = useState({ done: 0, total: 0 });
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [results, setResults]       = useState(null);   // null | []
  const [error, setError]           = useState(null); // solo para errores de carga inicial
  const [processedLoteId, setProcessedLoteId] = useState(null);
  const [hasCsv, setHasCsv]        = useState(false);
  const [supplFile, setSupplFile]   = useState(null);
  const supplRef = React.useRef(null);
  const pollRef = React.useRef(null);

  useEffect(() => {
    receiptsApi.lotes()
      .then(res => setLotes(res.data))
      .catch(() => setError('No se pudieron cargar los lotes'));
  }, []);

  // Responder a clicks del menú de navegación
  useEffect(() => {
    const handleFilter = (e) => navigate('/documents', { state: { filterId: e.detail?.filterId } });
    const handleSearch = (e) => navigate('/documents', { state: { search: e.detail?.text } });
    window.addEventListener('whl:filter', handleFilter);
    window.addEventListener('whl:search', handleSearch);
    return () => {
      window.removeEventListener('whl:filter', handleFilter);
      window.removeEventListener('whl:search', handleSearch);
    };
  }, [navigate]);

  // Limpiar polling al desmontar
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleProcess = async () => {
    if (!loteId) return;
    setProcessing(true);
    setResults(null);
    setHasCsv(false);
    setError(null);
    setProgress({ done: 0, total: 0 });

    try {
      const res = await receiptsApi.process(loteId, supplFile || undefined);
      const { jobId } = res.data;

      // Polling cada 2 segundos
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await receiptsApi.status(jobId);
          const job = statusRes.data;

          setProgress({ done: job.progress || 0, total: job.total || 0 });
          if (job.results?.length) setResults(job.results);

          if (job.status === 'done') {
            clearInterval(pollRef.current);
            setResults(job.results);
            setProcessedLoteId(parseInt(loteId));
            if (job.csvFilename) setHasCsv(true);
            setProcessing(false);
          } else if (job.status === 'error') {
            clearInterval(pollRef.current);
            showToast(job.error || t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
            setProcessing(false);
          }
        } catch {
          clearInterval(pollRef.current);
          showToast(t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
          setProcessing(false);
        }
      }, 2000);

    } catch (err) {
      showToast(err.response?.data?.error || t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
      setProcessing(false);
    }
  };

  const handlePublish = async () => {
    if (!processedLoteId) return;
    setPublishing(true);
    setError(null);
    try {
      const res = await receiptsApi.publish(processedLoteId);
      showToast(`${res.data.updated} ${t(TXT.RECORDS_PUBLISHED, 'fichas publicadas correctamente.')}`, 'success');
      setResults(null);
      setProcessedLoteId(null);
      setLoteId('');
      receiptsApi.lotes().then(r => setLotes(r.data)).catch(() => {});
    } catch (err) {
      showToast(err.response?.data?.error || t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async () => {
    if (!processedLoteId) return;
    if (!window.confirm(t(TXT.CONFIRM_DELETE_RECEIPTS, CONFIRM_DELETE_RECEIPTS_FALLBACK))) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await receiptsApi.deleteLote(processedLoteId);
      showToast(`${res.data.deleted} ${t(TXT.RECORDS_DELETED, 'fichas eliminadas correctamente.')}`, 'success');
      setResults(null);
      setProcessedLoteId(null);
      setHasCsv(false);
    } catch (err) {
      showToast(err.response?.data?.error || t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadCsv = () => {
    if (!processedLoteId) return;
    const token = localStorage.getItem('whl_token');
    const api   = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    fetch(`${api}/receipts/csv/${processedLoteId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a   = document.createElement('a');
        a.href     = url;
        a.download = `recibos_lote${processedLoteId}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => showToast(t(TXT.CSV_DOWNLOAD_ERROR, 'No se pudo descargar el CSV'), 'error'));
  };

  const buttonBar = null;

  const successCount = results ? results.filter(r => !r.error).length : 0;
  const errorCount   = results ? results.filter(r => r.error).length  : 0;

  return (
    <Layout buttonBar={buttonBar}>
      <h5 className="mb-3">{t(TXT.RECEIPTS_TITLE, 'Procesamiento de Recibos de Sueldo')}</h5>

      {/* Selector de lote + botones */}
      <div className="d-flex align-items-end gap-3 mb-4 flex-wrap">
        <div>
          <label className="form-label small mb-1">{t(TXT.SELECT_BATCH, 'Seleccionar lote')}</label>
          <CustomSelect
            style={{ minWidth: '320px' }}
            value={loteId}
            onChange={async e => {
              const id = e.target.value;
              setLoteId(id);
              setResults(null);
              setProcessedLoteId(null);
              setHasCsv(false);
              if (!id) return;
              try {
                const res = await receiptsApi.results(id);
                if (res.data.processed) {
                  setResults(res.data.results);
                  setProcessedLoteId(parseInt(id));
                  setHasCsv(true);
                }
              } catch { /* lote sin fichas, no hacer nada */ }
            }}
          >
            <option value="">— Seleccionar —</option>
            {lotes.map(l => (
              <option key={l.Doc_IdDocum} value={l.Doc_IdDocum}>
                {l.Doc_Titulo} ({l.Doc_FecAlta ? new Date(l.Doc_FecAlta).toLocaleDateString('es-AR') : ''})
              </option>
            ))}
          </CustomSelect>
        </div>

        <div>
          <label className="form-label small mb-1" style={{ color: '#888' }}>
            Datos suplementarios <span style={{ fontWeight: 400 }}>(opcional)</span>
          </label>
          <div className="d-flex align-items-center gap-2">
            <input
              type="file"
              accept=".txt,.csv,.tsv"
              ref={supplRef}
              className="form-control form-control-sm"
              style={{ width: 'auto', maxWidth: '240px' }}
              onChange={e => setSupplFile(e.target.files[0] || null)}
            />
            {supplFile && (
              <button className="btn btn-sm btn-outline-secondary" title="Quitar archivo"
                onClick={() => { setSupplFile(null); if (supplRef.current) supplRef.current.value = ''; }}>
                <i className="fa-solid fa-xmark" />
              </button>
            )}
          </div>
        </div>

        <button
          className="button color1"
          onClick={handleProcess}
          disabled={!loteId || processing}
        >
          {processing
            ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            : <i className="fa-solid fa-cogs fa-1x color1" />}
          &nbsp;&nbsp;{processing
            ? (progress.total > 0 ? `${t(TXT.PROCESSING, 'Procesando…')} ${progress.done}/${progress.total}` : t(TXT.PROCESSING, 'Procesando…'))
            : t(TXT.PROCESS, 'Procesar')}&nbsp;&nbsp;
        </button>

        {processedLoteId && !processing && (
          <>
            {hasCsv && (
              <button className="button color1" onClick={handleDownloadCsv}>
                <i className="fa-solid fa-file-csv fa-1x color1" />
                &nbsp;&nbsp;CSV&nbsp;&nbsp;
              </button>
            )}

            <button
              className="button color1"
              onClick={handlePublish}
              disabled={publishing || deleting}
            >
              {publishing
                ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                : <i className="fa-solid fa-globe fa-1x color1" />}
              &nbsp;&nbsp;{publishing ? t(TXT.PUBLISHING, 'Publicando…') : t(TXT.PUBLISH, 'Publicar')}&nbsp;&nbsp;
            </button>

            <button
              className="button"
              style={{ color: '#dc3545', borderColor: '#dc3545' }}
              onClick={handleDelete}
              disabled={publishing || deleting}
            >
              {deleting
                ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                : <i className="fa-solid fa-trash-can fa-1x" style={{ color: '#dc3545' }} />}
              &nbsp;&nbsp;{deleting ? t(TXT.DELETING, 'Eliminando…') : t(TXT.DELETE, 'Eliminar')}&nbsp;&nbsp;
            </button>
          </>
        )}
      </div>

      {/* Error global */}
      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      {/* Resultados */}
      {results && (
        <>
          <div className="mb-2 small text-muted">
            {successCount} ficha{successCount !== 1 ? 's' : ''} generada{successCount !== 1 ? 's' : ''}
            {errorCount > 0 && <span className="text-danger"> · {errorCount} error{errorCount !== 1 ? 'es' : ''}</span>}
          </div>
          <table className="table table-sm table-hover" style={{ fontSize: '0.85rem' }}>
            <thead className="table-light">
              <tr>
                <th>{t(TXT.CUIL, 'CUIL')}</th>
                <th>{t(TXT.LEGAJOS, 'Legajos')}</th>
                <th>{t(TXT.TITLE_LABEL, 'Nombre')}</th>
                <th>{t(TXT.PAGES, 'Páginas')}</th>
                <th>{t(TXT.USER_LABEL, 'Usuario')}</th>
                <th>{t(TXT.RECORD, 'Ficha')}</th>
                <th>{t(TXT.STATUS_LABEL, 'Estado')}</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={i} className={r.error ? 'table-danger' : ''}>
                  <td>{r.cuil}</td>
                  <td>{r.legajos ?? r.legajo ?? '—'}</td>
                  <td>{r.nombre}</td>
                  <td className="text-center">{r.pages ?? '—'}</td>
                  <td className="text-center">
                    {r.error ? '—' : (
                      r.hasUser
                        ? <i className="fa-solid fa-circle-check" style={{ color: '#2a9d8f' }} title="Usuario encontrado" />
                        : <i className="fa-solid fa-circle-xmark" style={{ color: '#aaa' }} title="Sin usuario" />
                    )}
                  </td>
                  <td>
                    {r.docId && (
                      <a
                        href="#"
                        onClick={e => { e.preventDefault(); navigate(`/documents/${r.docId}`); }}
                        style={{ fontSize: '0.8rem' }}
                      >
                        #{r.docId}
                      </a>
                    )}
                  </td>
                  <td>
                    {r.error
                      ? <span className="text-danger small">{r.error}</span>
                      : <span className="text-success small">OK</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Layout>
  );
}
